import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { openNotification } from '../UI';
import { CreateFileInput, CreateFilesArchiveInput, DeleteFileInput, File as GQLFile } from '../generated/graphql';
import { clientCreateFile, clientCreateFilesArchive, clientDeleteFile, useGraphqlClient } from '../graphql';
import { fileAttachmentQueryKeys } from '../FileAttachment/queryKeys';
import { fileQueryKeys } from './queryKeys';

export const useCreateFile = () => {
  const gqlClient = useGraphqlClient();
  const { t: tFile } = useTranslation('file');

  const createFile = useMutation({
    mutationFn: (input: CreateFileInput) => {
      return clientCreateFile(gqlClient, input);
    },
    onError: () => {
      openNotification({
        type: 'error',
        description: tFile('error-unable-to-create'),
      });
    },
  });

  return {
    createFile,
    createFileMutation: createFile,
  };
};

export const useCreateFilesArchive = () => {
  const gqlClient = useGraphqlClient();
  const { t: tFile } = useTranslation('file');

  const createFilesArchive = useMutation({
    mutationFn: (input: CreateFilesArchiveInput) => clientCreateFilesArchive(gqlClient, input),
    onError: () => {
      openNotification({
        type: 'error',
        description: tFile('error-unable-to-download'),
      });
    },
  });

  return {
    createFilesArchive,
    createFilesArchiveMutation: createFilesArchive,
  };
};

interface UseDeleteFileOptions {
  /**
   * When true, each successful delete only drops that file's detail cache; the
   * household file list and tenant attachment queries are NOT invalidated per
   * file. Use for bulk deletes and invalidate the lists once at the end via
   * `invalidateDeletedFileAttachments` (+ a single household refetch) to avoid a
   * refetch storm.
   */
  deferListInvalidation?: boolean;
}

export const useDeleteFile = (householdID?: string | null, options?: UseDeleteFileOptions) => {
  const gqlClient = useGraphqlClient();
  const queryClient = useQueryClient();

  const removeDeletedFileDetail = (input: DeleteFileInput) => {
    queryClient.removeQueries(fileQueryKeys.file(input.tenantID, input.id));
  };

  const invalidateDeletedFileAttachments = (tenantID: string) => {
    queryClient.invalidateQueries(fileAttachmentQueryKeys.fileAttachmentsForTenant(tenantID));
  };

  const reconcileDeletedFileCaches = (input: DeleteFileInput) => {
    removeDeletedFileDetail(input);
    if (householdID) queryClient.invalidateQueries(fileQueryKeys.filesForHousehold(householdID));
    invalidateDeletedFileAttachments(input.tenantID);
  };

  const deleteFile = useMutation({
    mutationFn: (input: DeleteFileInput) => clientDeleteFile(gqlClient, input),
    onSuccess: (_data, input) =>
      options?.deferListInvalidation ? removeDeletedFileDetail(input) : reconcileDeletedFileCaches(input),
  });

  return {
    deleteFile,
    deleteFileMutation: deleteFile,
    reconcileDeletedFileCaches,
    invalidateDeletedFileAttachments,
  };
};

interface UploadFileInput {
  gqlFile: GQLFile;
  file: File;
}

export const useUploadFile = () => {
  const { t: tFile } = useTranslation('file');

  const uploadFile = useMutation({
    mutationFn: async (input: UploadFileInput) => {
      const uploadUrl = input.gqlFile.uploadURL || '';
      const fileBuffer = await input.file.arrayBuffer();
      let contentType = input.file.type;
      if (contentType === 'text/plain') {
        contentType = 'text/plain; charset=utf-8';
      } else if (contentType === 'text/rtf') {
        contentType = 'text/rtf; charset=utf-8';
      }
      return axios.put(uploadUrl, fileBuffer, {
        headers: {
          'Content-Type': contentType,
        },
        timeout: 5000, // we will try for up to 5 sec
      });
    },
    onError: () => {
      openNotification({
        type: 'error',
        description: tFile('error-unable-to-upload'),
      });
    },
  });

  return {
    uploadFile,
    uploadFileMutation: uploadFile,
  };
};
